/**
 * governedConfigChrome.jsx
 *
 * Shared audit / integrity / publish-modal chrome used by governed config
 * editors (ConfigRegistry + IncidentCategoriesEditor).
 */
import React, { useState, useEffect } from 'react';
import { getConfigAudit, verifyConfigAudit } from '../api/client.js';

export function IntegrityVerifier() {
  const [state, setState] = useState({ status: 'idle' });
  async function check() {
    setState({ status: 'checking' });
    try {
      const r = await verifyConfigAudit();
      if (r.ok) setState({ status: 'ok', count: r.count });
      else setState({ status: 'broken', brokenId: r.brokenId, reason: r.reason, count: r.count });
    } catch (e) {
      setState({ status: 'error', reason: e.message || 'Verification request failed.' });
    }
  }
  const colour =
    state.status === 'ok' ? 'var(--ok)'
    : state.status === 'broken' || state.status === 'error' ? 'var(--danger)'
    : 'var(--text-3)';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <button className="btn ghost sm" onClick={check} disabled={state.status === 'checking'} style={{ fontSize: 11 }}>
        {state.status === 'checking' ? 'Verifying…' : 'Verify audit integrity'}
      </button>
      {state.status === 'ok' && (
        <span style={{ fontSize: 11, color: colour, alignSelf: 'center' }}>
          Chain intact — {state.count} {state.count === 1 ? 'row' : 'rows'} verified.
        </span>
      )}
      {state.status === 'broken' && (
        <span style={{ fontSize: 11, color: colour, alignSelf: 'center' }}>
          Tampering detected at row #{state.brokenId}: {state.reason}
        </span>
      )}
      {state.status === 'error' && (
        <span style={{ fontSize: 11, color: colour, alignSelf: 'center' }}>Could not verify: {state.reason}</span>
      )}
    </div>
  );
}

export function AuditPanel({ configKey, refreshToken, summarizeDiff }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { getConfigAudit(configKey).then(setRows); }, [configKey, refreshToken]);
  if (rows === null) return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading audit…</div>;
  if (rows.length === 0) return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No changes recorded yet.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map(r => {
        const summary = summarizeDiff ? summarizeDiff(r.diff) : defaultAuditSummary(r.diff);
        return (
          <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', background: 'var(--surface)' }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              {r.changedBy || 'unknown'} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>· {new Date(r.changedAt).toLocaleString()}</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{summary}</div>
          </div>
        );
      })}
    </div>
  );
}

function defaultAuditSummary(d) {
  const diff = d || {};
  const n = (diff.added?.length || 0) + (diff.removed?.length || 0) + (diff.changed?.length || 0);
  if (n === 0) return 'no field changes';
  return [
    diff.added?.length && `${diff.added.length} added`,
    diff.removed?.length && `${diff.removed.length} removed`,
    diff.changed?.length && `${diff.changed.length} edited`,
  ].filter(Boolean).join(' · ');
}

export function PublishConfirmModal({
  open, title, description, publishing, hasChanges, canPublish,
  onCancel, onDiscard, onPublish, children,
}) {
  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={() => !publishing && onCancel()}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)',
        padding: '20px 22px', maxWidth: 560, width: '100%', maxHeight: '80vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,.25)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{description}</div>
        {hasChanges
          ? children
          : <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 12 }}>No effective changes.</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} disabled={publishing} style={{ fontSize: 12 }}>Cancel</button>
          <button className="btn" onClick={onDiscard} disabled={publishing} style={{ fontSize: 12, color: 'var(--danger)' }}>Discard draft</button>
          <button className="btn primary" onClick={onPublish} disabled={publishing || !hasChanges || !canPublish} style={{ fontSize: 12 }}>
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
